import { CalendarEvent } from './calendarTypes';
import {
    formatDetailLabel,
    getEventDateLine,
    getEventDescription,
    getOptionalDetailEntries,
    stringifyCalendarValue,
} from './calendarUtils';

export type CalendarEventCardProps = {
    event: CalendarEvent;
    onRespond?: (event: CalendarEvent, response: 'yes' | 'maybe' | 'no') => void;
};

export function CalendarEventCard({ event, onRespond }: CalendarEventCardProps) {
    const description = getEventDescription(event);
    const optionalEntries = getOptionalDetailEntries(event);
    const canRespond = event._permissions?.respond === true;

    return (
        <article className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm transition hover:border-red-200 hover:shadow-md print:break-inside-avoid print:shadow-none">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                    <h3 className="text-lg font-bold text-gray-950">
                        {event.title}
                    </h3>

                    <p className="mt-1 text-sm font-semibold text-gray-600">
                        {getEventDateLine(event)}
                    </p>
                </div>

                {canRespond && (
                    <span className="rounded-full bg-red-100 px-3 py-1 text-xs font-bold text-red-800">
            Anmeldung aktiv
          </span>
                )}
            </div>

            {description && (
                <p className="mt-3 text-sm leading-6 text-gray-700">
                    {description}
                </p>
            )}

            {canRespond && (
                <div className="mt-4 flex flex-wrap gap-2 print:hidden">
                    <button
                        className="wwpwa-button-primary"
                        type="button"
                        onClick={() => onRespond?.(event, 'yes')}
                    >
                        Ja
                    </button>

                    <button
                        className="wwpwa-button-secondary"
                        type="button"
                        onClick={() => onRespond?.(event, 'maybe')}
                    >
                        Evtl
                    </button>

                    <button
                        className="rounded-xl bg-red-50 px-4 py-2 font-bold text-red-800 hover:bg-red-100"
                        type="button"
                        onClick={() => onRespond?.(event, 'no')}
                    >
                        Nein
                    </button>
                </div>
            )}

            {optionalEntries.length > 0 && (
                <details className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-3 print:hidden">
                    <summary className="cursor-pointer text-sm font-bold text-gray-800">
                        Weitere Details anzeigen
                    </summary>

                    <dl className="mt-3 grid gap-3 text-sm text-gray-700">
                        {optionalEntries.map(([key, value]) => (
                            <div key={key}>
                                <dt className="font-bold text-gray-950">
                                    {formatDetailLabel(key)}
                                </dt>
                                <dd className="mt-1 leading-6">
                                    {stringifyCalendarValue(value)}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </details>
            )}
        </article>
    );
}
